import {Model, ObjectHash} from 'backbone';
import $ from 'jquery';

import BenchlingCollection from 'BenchlingCollection';
import {ComponentsSequence} from 'components/Sequence';
import {SequenceFileState} from 'FileStates';
import * as logging from 'util/Logging';

export function sequenceIdFrom(xhr: XMLHttpRequest): string | null {
  return xhr.getResponseHeader('X-Sequence');
}

export function sequenceVersionIdFrom(xhr: XMLHttpRequest): number | null {
  const newSequenceVersionId = xhr.getResponseHeader('X-Sequence-Version');
  if (newSequenceVersionId != null) {
    return parseInt(newSequenceVersionId, 10);
  }
  return null;
}

export function skipSequenceVersionFrom(xhr: XMLHttpRequest): boolean {
  return xhr.getResponseHeader('X-Skip-Sequence-Version') === 'true';
}

type SequenceIdAndSequenceVersionId = [
  ReturnType<typeof sequenceIdFrom>,
  ReturnType<typeof sequenceVersionIdFrom>,
];

function handleTask(
  xhr: JQueryXHR,
  settings: JQueryAjaxSettings
): null | JQueryPromise<SequenceIdAndSequenceVersionId> {
  const taskEndpointMatch = /\/1\/api\/tasks\//.exec(settings.url!);
  if (taskEndpointMatch == null) {
    return null;
  }

  // Check response headers
  const sequenceEditPromise: JQueryDeferred<SequenceIdAndSequenceVersionId> = $.Deferred();
  xhr.done((res) => {
    if (res.status === 'SUCCESS') {
      sequenceEditPromise.resolve([sequenceIdFrom(xhr), sequenceVersionIdFrom(xhr)]);
    } else if (res.status === 'FAILURE') {
      sequenceEditPromise.reject([sequenceIdFrom(xhr), sequenceVersionIdFrom(xhr)]);
    }
  });
  return sequenceEditPromise.promise();
}

function handleSequenceEdit(
  xhr: JQueryXHR,
  settings: JQueryAjaxSettings
): null | JQueryPromise<SequenceIdAndSequenceVersionId> {
  const FileStates = require('FileStates').default;
  const fileStates = FileStates.get();
  const sequenceEndpointMatch = /\/1\/api\/sequences\/(\w+)\b/.exec(settings.url!);
  if (sequenceEndpointMatch == null || settings.type === 'GET') {
    return null;
  }

  const sequenceFileState = fileStates.get(sequenceEndpointMatch[1]) as SequenceFileState | undefined;
  if (sequenceFileState == null) {
    return null;
  }
  const sequence = sequenceFileState.model();

  // Add request headers
  Object.entries(SequenceAttributeCollection.requestHeaders(sequence)).forEach(([header, value]) => {
    xhr.setRequestHeader(header, value.toString());
  });

  // Check response headers
  const sequenceEditPromise: JQueryDeferred<SequenceIdAndSequenceVersionId> = $.Deferred();
  xhr.done((res) => {
    const sequenceId = sequenceIdFrom(xhr);
    const sequenceVersionId = sequenceVersionIdFrom(xhr);
    if (sequenceVersionId == null && !skipSequenceVersionFrom(xhr)) {
      logging.error(`Missing X-Sequence-Version from ${settings.type} ${settings.url}`);
    }
    sequenceEditPromise.resolve([sequenceId, sequenceVersionId]);
  });
  xhr.fail((res) => sequenceEditPromise.reject([sequenceIdFrom(xhr), sequenceVersionIdFrom(xhr)]));
  return sequenceEditPromise.promise();
}

class SequenceAttributeCollection<M extends Model, MAttrs extends ObjectHash> extends BenchlingCollection<
  M,
  MAttrs
> {
  attributeName!: null | string;
  sequenceID?: string;

  static initClass(): void {
    // Must override attributeName.
    this.prototype.attributeName = null;
  }

  static requestHeaders(sequence?: ComponentsSequence | null): {'X-Sequence-Version'?: number} {
    if (sequence == null) {
      return {};
    }
    return {
      'X-Sequence-Version': sequence._sequence().latestVersionId(),
    };
  }

  constructor(
    models: Array<M> | Array<Partial<MAttrs>> = [],
    options?: Backbone.Parseable & {sequenceID?: string}
  ) {
    super(models, options);
  }

  static sequenceVersionSetter(xhr: JQueryXHR, settings: JQueryAjaxSettings): void {
    const fileStates = require('FileStates').default.get();
    const sequenceEditPromise = handleTask(xhr, settings) || handleSequenceEdit(xhr, settings);
    if (sequenceEditPromise == null) {
      return;
    }

    sequenceEditPromise
      .done(([sequenceId, sequenceVersionId]: SequenceIdAndSequenceVersionId) => {
        if (sequenceId == null || sequenceVersionId == null) {
          return;
        }
        (fileStates.get(sequenceId) as SequenceFileState | undefined)
          ?.model()
          .setLatestVersionId(sequenceVersionId, {activateIfOnCurrent: true});
      })
      .fail(([sequenceId, sequenceVersionId]: SequenceIdAndSequenceVersionId) => {
        if (sequenceId == null || sequenceVersionId == null) {
          return;
        }
        const sequence = (fileStates.get(sequenceId) as SequenceFileState | undefined)?.model();
        if (sequence != null) {
          // We had an edit conflict with another browser tab, so re-fetch the sequence history and
          // update to latest. The server error will include a user-facing message that we will
          // separately show in a red toast to explain what happened.
          sequence.updateVersions({switchToLast: true, showHistoryPanel: false});
        }
      });
  }

  override url = (): string => {
    if (this.sequenceID == null) {
      throw new Error('@sequenceID was not defined in SequenceAttributeCollection but URL was needed.');
    }
    return `/1/api/sequences/${this.sequenceID}/${this.attributeName!}`;
  };

  override initialize(models: Array<M>, param?: {sequenceID: string}): void {
    if (param != null) {
      this.sequenceID = param.sequenceID;
    }
    if (this.attributeName == null) {
      throw new Error('SequenceAttributeCollection was missing @attributeName');
    }
  }
}

SequenceAttributeCollection.initClass();

export default SequenceAttributeCollection;
